/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ButtonERP,
  ButtonFilterERP,
  Checkbox,
  SearchInput,
  TagERP,
} from "@finanedi.es/storybook"
import {
  faCircleCheck,
  faCircleXmark,
  faClock,
} from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { CesionQueryParamsProps } from "api/Cesion.service"
import { SortingCriteria } from "api/interfaces/SortingCriteria"
import { ESTADO_CESION, EstadoCesionType } from "api/Types/EstadoCesion"
import { INIITAL_ESTADO_REVISION } from "features/Validaciones/constants"
import { AnimatePresence } from "framer-motion"
import { Dialog } from "primereact/dialog"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { CESIONES_DEFAULT_STATUS } from "./constants/CesionesDefaultStatus"

const FilterMenu = ({
  serviceQuery,
  sortingCriteria,
  skipCount,
  maxResultCount,
  resetPagination,
}: {
  serviceQuery: $TSMigrationFixMe
  sortingCriteria?: SortingCriteria
  skipCount?: CesionQueryParamsProps["SkipCount"]
  maxResultCount?: CesionQueryParamsProps["MaxResultCount"]
  resetPagination: () => void
}) => {
  const { t } = useTranslation()
  const [keyword, setKeyword] = useState("")
  const [menuOpen, setMenuOpen] = useState(false)

  const [estadoRevision, setEstadoRevision] = useState<
    Record<EstadoCesionType, boolean>
  >(INIITAL_ESTADO_REVISION)

  const openFilters = () => {
    setMenuOpen(true)
  }
  const queryFixer = (queryParams: CesionQueryParamsProps) => {
    const facturasParams = {
      EmpresaId: queryParams.EmpresaId,
      Estados: queryParams.Estados,
      Keyword: queryParams.Keyword,
      MaxResultCount: queryParams.MaxResultCount,
      SkipCount: queryParams.SkipCount,
      SortingCriteria:
        queryParams.SortingCriteria?.property &&
        queryParams.SortingCriteria?.sort
          ? `${queryParams.SortingCriteria.property} ${queryParams.SortingCriteria.sort}`
          : "",
    }
    const fixedQueryParams = Object.fromEntries(
      Object.entries(facturasParams).filter(([_key, value]) => value) // eslint-disable-line @typescript-eslint/no-unused-vars
    )
    return fixedQueryParams
  }

  const handleQuery = () => {
    const estadosRevision = Object.entries(estadoRevision)
      .filter(([_key, value]) => value) // eslint-disable-line @typescript-eslint/no-unused-vars
      .flatMap(([key]) => key) as unknown as EstadoCesionType[]
    serviceQuery(
      queryFixer({
        Estados:
          estadosRevision.length > 0
            ? estadosRevision
            : CESIONES_DEFAULT_STATUS,
        Keyword: keyword,
        SortingCriteria: {
          property: sortingCriteria?.property ?? "Estado",
          sort: sortingCriteria?.sort ?? "ASC",
        },
        MaxResultCount: maxResultCount,
        SkipCount: skipCount,
      })
    )
  }

  const handleApplyFilters = () => {
    handleQuery()
    setMenuOpen(false)
  }
  const hideFilter = () => {
    setMenuOpen(!menuOpen)
  }

  const handleResetFilters = () => {
    serviceQuery(
      queryFixer({
        SortingCriteria: {
          property: sortingCriteria?.property ?? "Estado",
          sort: sortingCriteria?.sort ?? "ASC",
        },
        Estados: CESIONES_DEFAULT_STATUS,
      })
    )
    setEstadoRevision(INIITAL_ESTADO_REVISION)
  }

  const toggleReviewedStatus = (
    keys: EstadoCesionType | EstadoCesionType[]
  ) => {
    const keysArray = Array.isArray(keys) ? keys : [keys]

    setEstadoRevision((prev) => {
      const updated = { ...prev }
      keysArray.forEach((key) => {
        updated[key] = !prev?.[key]
      })
      return updated
    })
  }

  const appliedFiltersTags = [
    {
      label: `${t("filterMenu.appliedTag.statusTag")}`,
      value:
        estadoRevision?.[ESTADO_CESION.CREADA] ||
        estadoRevision?.[ESTADO_CESION.COMUNICADA]
          ? t("filterMenu.invoice.pendingLabel")
          : undefined,
      action: () => {
        toggleReviewedStatus(ESTADO_CESION.CREADA)
        toggleReviewedStatus(ESTADO_CESION.COMUNICADA)
      },
    },
    {
      label: `${t("filterMenu.appliedTag.statusTag")}`,
      value: estadoRevision?.[ESTADO_CESION.ACEPTADA]
        ? t("filterMenu.invoice.approvedLabel")
        : undefined,
      action: () => toggleReviewedStatus(ESTADO_CESION.ACEPTADA),
    },
    {
      label: `${t("filterMenu.appliedTag.statusTag")}`,
      value: estadoRevision?.[ESTADO_CESION.RECHAZADA]
        ? t("filterMenu.invoice.rejectedLabel")
        : undefined,
      action: () => toggleReviewedStatus(ESTADO_CESION.RECHAZADA),
    },
  ]
  const appliedFilters = appliedFiltersTags.filter((filter) => filter.value)

  useEffect(() => {
    resetPagination?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, appliedFilters?.length])

  useEffect(() => {
    handleQuery()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sortingCriteria?.sort,
    sortingCriteria?.property,
    skipCount,
    maxResultCount,
    appliedFilters.length,
    keyword,
  ])

  return (
    <>
      <div className='tw-flex tw-flex-row tw-gap-3 tw-h-10'>
        <ButtonFilterERP
          isActive={appliedFilters.length > 0}
          numberActive={appliedFilters?.length}
          onClick={openFilters}
          deleteFilter={handleResetFilters}
          filterButtonLabel={`${t("filtros.title")}`}
        />
        <SearchInput
          placeholder={`${t("validaciones.placeholderSearch")}`}
          onChange={(ev: string) => {
            resetPagination()
            setKeyword(ev)
          }}
          inputValue={keyword}
          className='tw-h-10 tw-w-full sm:tw-w-72'
        />
      </div>

      <AnimatePresence>
        <Dialog
          breakpoints={{ "960px": "75vw" }}
          className='p-dialog-right'
          header={t("filtros.title")}
          visible={menuOpen}
          footer={
            <div className='tw-w-full tw-border-t-2 tw-pt-6'>
              <ButtonERP
                customize={{
                  type: "primary",
                  size: "md",
                }}
                className='!tw-w-full'
                onClick={handleApplyFilters}
                label={`${t("filtros.labelApply")}`}
              />
            </div>
          }
          onHide={() => hideFilter()}
          draggable={false}
          resizable={false}
          position='right'
          closable
          closeOnEscape
        >
          {!!appliedFilters?.length && (
            <div className='tw-mb-8'>
              <div className='flex tw- tw-mb-4 tw-justify-between'>
                <span className='tw-font-medium'>
                  {t("filterMenu.appliedTag.appliedFilters")}
                </span>
                <span
                  onClick={handleResetFilters}
                  className='tw-text-text-hyperlinks tw-cursor-pointer'
                >
                  {t("filterMenu.appliedTag.deleteAll")}
                </span>
              </div>
              <div className='tw-flex tw-flex-wrap tw-mb-8 tw-gap-2 tw-font-normal tw-text-xs'>
                {appliedFilters.map((filter) => (
                  <div
                    key={`${filter.label}-${filter.value}`}
                    onClick={filter.action}
                    className='tw-items-center tw-justify-between tw-cursor-pointer tw-bg-info-10 tw-text-info tw-rounded-md tw-border tw-border-info tw-py-1 tw-px-2'
                  >
                    <span>
                      {filter.label} {filter.value}
                    </span>
                    <FontAwesomeIcon
                      icon={["fal", "xmark"]}
                      className='tw-ml-2 tw-text-info'
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className='tw-mb-8'>
            <div className='tw-mb-4'>
              <span className='tw-font-medium'>
                {t("filterMenu.invoice.status")}
              </span>
            </div>

            <div className='tw-flex tw-items-center tw-mb-4'>
              <Checkbox
                id='pending'
                className='tw-mr-4'
                isChecked={
                  estadoRevision?.[ESTADO_CESION.CREADA] ||
                  estadoRevision?.[ESTADO_CESION.COMUNICADA]
                }
                onClick={() => {
                  toggleReviewedStatus([
                    ESTADO_CESION.CREADA,
                    ESTADO_CESION.COMUNICADA,
                  ])
                }}
              />
              <TagERP
                value={t("filters.estadoRevision.pendingLabel")}
                color='alternativeOne'
                icon={<FontAwesomeIcon icon={faClock} />}
              />
            </div>
            <div className='tw-flex tw-items-center tw-mb-4'>
              <Checkbox
                id='validated'
                className='tw-mr-4'
                isChecked={estadoRevision?.[ESTADO_CESION.ACEPTADA]}
                onClick={() => toggleReviewedStatus(ESTADO_CESION.ACEPTADA)}
              />
              <TagERP
                value={t("filters.estadoRevision.validateLabel")}
                color='success'
                icon={<FontAwesomeIcon icon={faCircleCheck} />}
              />
            </div>
            <div className='tw-flex tw-items-center tw-mb-4'>
              <Checkbox
                id='rejected'
                className='tw-mr-4'
                isChecked={estadoRevision?.[ESTADO_CESION.RECHAZADA]}
                onClick={() => toggleReviewedStatus(ESTADO_CESION.RECHAZADA)}
              />
              <TagERP
                value={t("filters.estadoRevision.rejectedLabel")}
                color='danger'
                icon={<FontAwesomeIcon icon={faCircleXmark} />}
              />
            </div>
          </div>
        </Dialog>
      </AnimatePresence>
    </>
  )
}

export default FilterMenu
